import { clientApi } from "@/utils/api";
import {
  TSharingEntityType,
  TUpsertSharingReqDto,
  TUpsertSharingResDto,
  TVerifySharingReqDto,
  TVerifySharingResDto,
  TGetSharingResDto,
  TGetSharingDetailResDto,
} from "@/api/services/main/sharing/dtos/sharing.dto";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { TApiPagRes } from "@/types/api";
import { TSharedCaseMatch } from "@/features/sharing/types/shared-case";
import { TGetSingedUrlResDto } from "@/api/services/global/file/dtos/get-signed-url";
import { createUrlWithParams } from "@/utils/url";

/** PATCH /sharing/:entityType/:entityId — Create or update share link */
export const upsertSharingApi = async (
  entityType: TSharingEntityType,
  entityId: number | string,
  payload: TUpsertSharingReqDto,
): Promise<TUpsertSharingResDto> => {
  const { data } = await clientApi.patch<TUpsertSharingResDto>(`/sharing/${entityType}/${entityId}`, payload);
  return data;
};

/** POST /sharing/verify — Verify password for shared link */
export const verifySharingApi = async (payload: TVerifySharingReqDto): Promise<TVerifySharingResDto> => {
  const { data } = await clientApi.post<TVerifySharingResDto>("/sharing/verify", payload);
  return data;
};

/** GET /sharing/:entityType/:entityId — Get share link info */
export const getSharingApi = async (entityType: TSharingEntityType, entityId: number | string, shareType?: number): Promise<TGetSharingResDto> => {
  const params = shareType !== undefined ? `?shareType=${shareType}` : "";
  const { data } = await clientApi.get<{ data: TGetSharingResDto }>(`/sharing/${entityType}/${entityId}${params}`);
  return data.data;
};

/** Fetch existing sharing info for an entity (authenticated) */
export const useSharingInfo = (entityType: TSharingEntityType, entityId?: string | number, shareType?: number) => {
  return useQuery({
    queryKey: [QUERY_KEYS.SHARING.FETCH_INFO, entityType, entityId, shareType],
    queryFn: () => getSharingApi(entityType, entityId!, shareType),
    enabled: !!entityId,
    retry: false,
  });
};

/** GET /sharing/detail/:shareUrl — Get public share link detail (no auth) */
export const getSharingDetailApi = async (shareUrl: string): Promise<TGetSharingDetailResDto> => {
  const { data } = await clientApi.get<TGetSharingDetailResDto>(`/sharing/detail/${shareUrl}`);
  return data;
};

export const useSharingDetail = (shareUrl?: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.SHARING.FETCH_DETAIL, shareUrl],
    queryFn: () => getSharingDetailApi(shareUrl!),
    enabled: !!shareUrl,
    retry: false,
  });
};

/** GET /sharing/talents/:talentId/download-resume?shareUrl=:shareUrl — Download resume for shared view (no auth) */
export const downloadSharedResumeApi = async (talentId: number, shareUrl: string) => {
  return await clientApi.get(`/sharing/talents/${talentId}/download-resume?shareUrl=${shareUrl}`, {
    responseType: "blob",
  });
};

/** GET /sharing/download-all-resumes?shareUrl=:shareUrl — Bulk download all resumes for shared case (no auth) */
export const downloadSharedAllResumesApi = async (shareUrl: string) => {
  return await clientApi.get(`/sharing/download-all-resumes?shareUrl=${shareUrl}`, {
    responseType: "blob",
  });
};

/** GET /sharing/export-csv-shared-talents?shareUrl=:shareUrl - Export shared case match talents as CSV (no auth) */
export const exportSharedCaseMatchTalentsAsCsvApi = async (shareUrl: string) => {
  return await clientApi.get(`/sharing/export-csv-shared-talents?shareUrl=${shareUrl}`, {
    responseType: "blob",
  });
};

/** GET /sharing/talents/:talentId?shareUrl=:shareUrl — Get talent data for shared case (no auth) */
export const fetchSharedTalentApi = async (talentId: number, shareUrl: string) => {
  const { data } = await clientApi.get(`/sharing/talents/${talentId}?shareUrl=${shareUrl}`);
  return data;
};

export const useSharedTalentInfo = (talentId?: number, shareUrl?: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.SHARING.FETCH_TALENT, talentId, shareUrl],
    queryFn: () => fetchSharedTalentApi(talentId!, shareUrl!),
    enabled: !!talentId && !!shareUrl,
  });
};

/** GET /sharing/case-matches?shareUrl=:shareUrl&page=:page&perPage=:perPage — Paginated case matches for shared view (no auth) */
export const fetchSharedCaseMatchesApi = async (
  shareUrl: string,
  params: { page: number; perPage: number; sorts?: string },
): Promise<TApiPagRes<TSharedCaseMatch>> => {
  const searchParams = new URLSearchParams({
    shareUrl,
    page: String(params.page),
    perPage: String(params.perPage),
  });
  if (params.sorts) searchParams.set("sorts", params.sorts);
  const { data } = await clientApi.get<TApiPagRes<TSharedCaseMatch>>(`/sharing/case-matches?${searchParams.toString()}`);
  return data;
};

export const useSharedCaseMatches = (shareUrl?: string, params?: { page: number; perPage: number; sorts?: string }) => {
  return useQuery({
    queryKey: [QUERY_KEYS.SHARING.FETCH_CASE_MATCHES, shareUrl, params?.page, params?.perPage, params?.sorts],
    queryFn: () => fetchSharedCaseMatchesApi(shareUrl!, params!),
    enabled: !!shareUrl && !!params,
    placeholderData: keepPreviousData,
    // Always fetch fresh data — internal users may update case match status at any time
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
  });
};

/** GET /sharing/files/signed-url?filePath=:filePath&shareUrl=:shareUrl — Get signed URL for shared view (no auth) */
export const fetchSharedSignedUrlApi = async (filePath: string, shareUrl: string): Promise<TGetSingedUrlResDto> => {
  const url = createUrlWithParams("/sharing/files/signed-url", { filePath, shareUrl });
  const { data } = await clientApi.get<TGetSingedUrlResDto>(url);
  return data;
};

export const useSharedFileSignedUrl = (filePath?: string | null, shareUrl?: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.SHARING.FETCH_SIGNED_URL, filePath, shareUrl],
    queryFn: () => fetchSharedSignedUrlApi(filePath!, shareUrl!),
    enabled: !!filePath && !!shareUrl,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};
